/** A string key-value store. In the browser it's localStorage; tests pass a Map-backed one. */
export interface KeyValue {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** True when it only lasts until the page closes (localStorage is blocked). */
  temporary?: boolean;
}

/** JSON read that never throws (missing, blocked or corrupt → null), like the current app's `lsGet`. */
export function readJson<T>(store: KeyValue, key: string): T | null {
  try {
    return JSON.parse(store.getItem(key) ?? "null") as T | null;
  } catch {
    return null;
  }
}

/** JSON write that never throws; null removes the key (`lsSet`). Says whether it saved. */
export function writeJson(store: KeyValue, key: string, value: unknown): boolean {
  try {
    if (value == null) store.removeItem(key);
    else store.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // Storage full or blocked (private mode): the current app carries on too.
    return false;
  }
}

/** The browser's localStorage, or an in-memory stand-in when it's blocked. */
export function browserStorage(): KeyValue {
  try {
    const ls = globalThis.localStorage;
    ls.setItem("hachi-garage-v1-test", "1");
    ls.removeItem("hachi-garage-v1-test");
    return ls;
  } catch {
    return { ...memoryStorage(), temporary: true };
  }
}

export function memoryStorage(seed: Record<string, string> = {}): KeyValue {
  const m = new Map(Object.entries(seed));
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
}
